import { usePredefinedMessage } from '@/container/daycare/daycareProfile/customisation/predefinedMessage/usePredefinedMessage'
import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { OccasionalSlotsPlaces, RegularSlot, slotIdAndDateAndChildId } from '@/types/Slots'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
    DECLARE_ABSENCE,
    DECLARE_MULTIPLE_ABSENCE,
    REQUEST_MULTIPLE_ABSENCE_REGULAR,
    REQUEST_UPDATE_REGULAR
} from '@/modules/API/slot/regularUpdate'
import moment, { Moment } from 'moment/moment'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import styles from './declareAbsenceRegularSlot.module.scss'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import TextArea from '@/components/_custom/TextArea/textArea'
import { CalendarMonth, Close, DoDisturbOn, Transform } from '@mui/icons-material'
import TransformRegularSlot from '../transformRegularSlot/transformRegularSlot'
import { Daycare, DaycarePredefinedMessageType } from '@/types/Daycare'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import SelectMultipleDateDialogComponent from '../../occasionalSlot/occasionalSlotForm/components/SelectMultipleDateDialog/selectMultipleDateDialog.tsx'
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderInputParams,
    Chip,
    TextField
} from '@mui/material'
import { format } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { GET_REGULAR_SLOTS } from '@/modules/API/slot/regularSlotHandler.ts'
import { CalendarEventType } from '@/types/Calendar.ts'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker.tsx'
import { getDaycareDaysSelectOptions } from '@/modules/helper/daycareHelper.ts'
import { getDatesBetweenTwoDates } from '../confirmDeleteRegularSlot/confirmDeleteRegularSlotDialog.tsx'
import { ClosingDay } from '@/types/ClosingDays.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useDialog } from '@/hooks/useDialog/useDialog.tsx'
import { DAY_FORMAT } from '../../occasionalSlot/models/constants.ts'

interface DeclareAbsenceRegularSlotProps {
    open: boolean
    handleClose: () => void
    isParent: boolean
    regularSlots: RegularSlot[]
    places: OccasionalSlotsPlaces[]
    date: Date | null
    closeParent: () => void
    refetchData: () => void
    daycare: Daycare
    isRpeOrLaep: boolean
    lastRegularSlotDate: string
    closingDays: ClosingDay[]
}

const DeclareAbsenceRegularSlot: FunctionComponent<DeclareAbsenceRegularSlotProps> = ({
    open,
    handleClose,
    isParent,
    regularSlots,
    date,
    closeParent,
    refetchData,
    daycare,
    isRpeOrLaep,
    places,
    lastRegularSlotDate,
    closingDays
}: DeclareAbsenceRegularSlotProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [isTransformRegularSlotDialogOpen, setTransformRegularSlotDialogOpen] = useState(false)

    const [chooseDate, setChooseDate] = useState<boolean>(false)
    const [dateList, setDateList] = useState<string[]>([])

    const [selectPeriod, setSelectPeriod] = useState<boolean>(false)
    const [startDate, setStartDate] = useState<string | null>(date ? moment(date).format('DD-MM-YYYY') : null)
    const [endDate, setEndDate] = useState<string | null>(date ? moment(date).format('DD-MM-YYYY') : null)

    const [chooseDays, setChooseDays] = useState<boolean>(false)
    const [selectedDays, setSelectedDays] = useState<string[]>([])

    const [selectChilds, setSelectChilds] = useState<string[]>(
        regularSlots.map((slot: RegularSlot): string => slot.childId.toString())
    )

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options: regularSlots.map((slot: RegularSlot): SelectOptionDeprecated => {
                return {
                    name: slot.childName + (slot.childComplement ? ' - ' + slot.childComplement : ''),
                    value: slot.childId.toString()
                }
            })
        }
    ]

    const selectDays: SelectOptionsDeprecated = getDaycareDaysSelectOptions(daycare, t)

    const {
        message: reason,
        textAreaProps,
        dialog: predefinedMessageDialog
    } = usePredefinedMessage(DaycarePredefinedMessageType.declareAbsenceRegularSlot, {
        daycare,
        allowEditDefault: !isParent
    })

    const [declareAbsence, { loading: loadingDeclare }] = useMutation(DECLARE_ABSENCE, {
        onCompleted: () => {
            showNotificationSuccess(t('absenceDeclared'))

            handleClose()
            closeParent()
            refetchData()
        },
        onError: () => {
            showNotificationError(t('errorDeclaredAbsence'))
        }
    })

    const [declareMultipleAbsence, { loading: loadingMultipleDeclare }] = useMutation(DECLARE_MULTIPLE_ABSENCE, {
        onCompleted: () => {
            showNotificationSuccess(t('absenceDeclared'))

            handleClose()
            closeParent()
            refetchData()
        },
        onError: () => {
            showNotificationError(t('errorDeclaredAbsence'))
        }
    })

    function declareAllAbsences(): void {
        if ((chooseDate && dateList.length > 0) || selectPeriod) {
            const slotToRequest: slotIdAndDateAndChildId[] = []

            const dateListToDelete: string[] =
                selectPeriod && startDate && endDate
                    ? getDatesBetweenTwoDates(startDate, endDate, selectedDays, closingDays)
                    : dateList

            dateListToDelete.forEach((date: string) => {
                regularSlotsInCalendar
                    .filter((slot: slotIdAndDateAndChildId): boolean => selectChilds.includes(slot.childId.toString()))
                    .filter((slot: slotIdAndDateAndChildId): boolean => slot.date === date)
                    .forEach((slot: slotIdAndDateAndChildId) => {
                        slotToRequest.push(slot)
                    })
            })

            declareMultipleAbsence({
                variables: {
                    daycareId: daycare.id,
                    slots: slotToRequest,
                    daycareComment: reason
                }
            })
        } else {
            regularSlots
                .filter((slot: RegularSlot): boolean => selectChilds.includes(slot.childId.toString()))
                .forEach((slot: RegularSlot): void => {
                    declareAbsence({
                        variables: {
                            regular_slot_id: slot.id,
                            date: moment(date).format('DD-MM-YYYY'),
                            daycare_id: slot.daycareId,
                            daycare_comment: reason ? reason : null
                        }
                    })
                })
        }
    }

    const [requestAbsence, { loading: loadingRequest }] = useMutation(REQUEST_UPDATE_REGULAR, {
        onCompleted: () => {
            showNotificationSuccess('success')

            handleClose()
            closeParent()
            refetchData()
        },
        onError: () => {
            showNotificationError(t('errorRequestedAbsence'))
        }
    })

    const [requestMultipleAbsence, { loading: loadingMultipleRequest }] = useMutation(
        REQUEST_MULTIPLE_ABSENCE_REGULAR,
        {
            onCompleted: () => {
                showNotificationSuccess('success')

                handleClose()
                closeParent()
                refetchData()
            },
            onError: () => {
                showNotificationError('error')
            }
        }
    )

    function requestAllAbsences(): void {
        if ((chooseDate && dateList.length > 0) || selectPeriod) {
            const slotToRequest: slotIdAndDateAndChildId[] = getSlotToRequestAllAbsences(
                selectChilds,
                selectPeriod,
                startDate,
                endDate,
                selectedDays,
                dateList,
                regularSlotsInCalendar,
                closingDays
            )

            requestMultipleAbsence({
                variables: {
                    daycareId: daycare.id,
                    slots: slotToRequest,
                    parentComment: reason
                }
            })
        } else {
            regularSlots
                .filter((slot: RegularSlot): boolean => selectChilds.includes(slot.childId.toString()))
                .forEach((slot: RegularSlot): void => {
                    requestAbsence({
                        variables: {
                            regular_slot_id: slot.id,
                            date: moment(date).format('DD-MM-YYYY'),
                            start: moment(slot.start, 'HH:mm:ss').format('HH:mm'),
                            end: moment(slot.end, 'HH:mm:ss').format('HH:mm'),
                            reason: reason,
                            daycare_id: slot.daycareId
                        }
                    })
                })
        }
    }

    function handleCheckChooseDate(checked: boolean): void {
        if (dateList.length === 0) setDateList([moment(date).format('DD-MM-YYYY')])
        if (checked) openSelectMultipleDateDialog()
        setChooseDate(checked)

        if (selectPeriod) {
            setSelectPeriod(false)
            setStartDate(null)
            setEndDate(null)
        }
    }

    function handleDateRemove(dateToRemove: string): void {
        setDateList(dateList.filter((date) => date !== dateToRemove))
    }

    function handleCheckSelectPeriod(checked: boolean): void {
        if (startDate === null || endDate === null) {
            setStartDate(moment(date).format('DD-MM-YYYY'))
            setEndDate(moment(date).format('DD-MM-YYYY'))
        }

        setSelectPeriod(checked)

        if (chooseDate) {
            setChooseDate(false)
            setDateList([])
        }
    }

    const [otherDisabledDates, setOtherDisabledDates] = useState<Date[]>([])

    const [regularSlotsInCalendar, setRegularSlotsInCalendar] = useState<slotIdAndDateAndChildId[]>([])

    const [getRegularSlots, { data: dataRegularSlots, loading: loadingRegularSlots }] = useLazyQuery(GET_REGULAR_SLOTS)

    useEffect(() => {
        getRegularSlots({
            variables: {
                from: moment().format('DD-MM-YYYY'),
                to: lastRegularSlotDate,
                daycareId: daycare.id,
                childIds: selectChilds.map((childId: string): number => Number(childId))
            }
        })
    }, [selectChilds])

    useEffect(() => {
        const dateToShow: string[] = []
        const regularSlotsCalendar: slotIdAndDateAndChildId[] = []

        dataRegularSlots?.getRegularSlots.map((regularSlot: RegularSlot): void => {
            if (regularSlot.type === CalendarEventType.REGULAR) {
                dateToShow.push(regularSlot.date)

                regularSlotsCalendar.push({
                    slotId: regularSlot.id,
                    date: regularSlot.date,
                    childId: regularSlot.childId
                })
            }
        })

        setRegularSlotsInCalendar(regularSlotsCalendar)

        const start: Moment = moment().startOf('month')
        const end: Moment = start.clone().add(1, 'year').endOf('month')

        const result: Date[] = []

        let currentDate: Moment = start.clone()
        while (currentDate.isSameOrBefore(end)) {
            const formatted: string = currentDate.format('DD-MM-YYYY')
            if (!dateToShow.includes(formatted)) {
                result.push(currentDate.toDate())
            }
            currentDate = currentDate.add(1, 'days')
        }

        setOtherDisabledDates(result)
    }, [dataRegularSlots])

    const { dialog: SelectMultipleDateDialog, openDialog: openSelectMultipleDateDialog } = useDialog({
        Component: SelectMultipleDateDialogComponent,
        props: () => ({
            daycare,
            dateList,
            setDateList,
            startDate: moment().format(DAY_FORMAT),
            closingDays,
            isParent,
            otherDisabledDates
        }),
        dialogTitle: false
    })

    return (
        <>
            <Dialog open={open} handleClose={handleClose}>
                <DialogHeader title={isParent ? t('requestAbsence') : t('declareAbsence')} parent={isParent} />
                <DialogBody className={styles.DialogBody}>
                    <div className={styles.Container}>
                        {chooseDate && dateList.length > 0 ? (
                            <Autocomplete
                                multiple
                                options={dateList}
                                value={dateList}
                                onChange={(_event: SyntheticEvent, newValue: string[]) => setDateList(newValue)}
                                renderTags={(value: string[], getTagProps: AutocompleteRenderGetTagProps) =>
                                    value.map((option: string, index: number) => {
                                        const { key, ...tagProps } = getTagProps({ index })

                                        return (
                                            <Chip
                                                key={key}
                                                label={option}
                                                {...tagProps}
                                                onDelete={() => handleDateRemove(option)}
                                            />
                                        )
                                    })
                                }
                                renderInput={(params: AutocompleteRenderInputParams) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        rows={1}
                                        multiline={true}
                                        label={t('dateList')}
                                    />
                                )}
                                className={isParent ? styles.AutocompleteParent : styles.AutocompleteDaycare}
                                onOpen={openSelectMultipleDateDialog}
                            />
                        ) : selectPeriod && startDate && endDate ? (
                            <div className={styles.InputContainer}>
                                <div className={styles.Input}>
                                    <DatePickerComponent
                                        className={styles.UserInput}
                                        value={startDate.valueOf()}
                                        setValue={setStartDate}
                                        maxDate={endDate.valueOf()}
                                        disablePast={true}
                                        label={t('fromDate')}
                                        parent={isParent}
                                    />
                                    <DatePickerComponent
                                        className={styles.UserInput}
                                        value={endDate.valueOf()}
                                        setValue={setEndDate}
                                        minDate={startDate.valueOf()}
                                        label={t('toDate')}
                                        parent={isParent}
                                    />
                                </div>
                                <div className={styles.VerticalInput}>
                                    <div className={styles.CheckboxInput}>
                                        <Checkbox
                                            text={t('chooseRecurrence')}
                                            checked={chooseDays}
                                            onChange={(checked: boolean) => setChooseDays(checked)}
                                            className={styles.Checkbox}
                                            parent={isParent}
                                        />
                                    </div>
                                    {chooseDays && (
                                        <SelectMultiple
                                            selectOptions={selectDays}
                                            label={t('days')}
                                            selectValues={selectedDays}
                                            setSelectValues={setSelectedDays}
                                            className={styles.UserInput}
                                            isParent={isParent}
                                        />
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                <CalendarMonth
                                    className={`${styles.Icon} ${isParent ? styles.IconParent : styles.IconDaycare}`}
                                />
                                <div className={styles.TextContainer}>
                                    {date && (
                                        <p className={styles.FirstText}>
                                            {format(date, 'EEEE dd MMMM yyyy', {
                                                locale: getLocalFromLanguage()
                                            })}
                                        </p>
                                    )}
                                    <p className={styles.SecondText}>
                                        {t('from') +
                                            ' ' +
                                            moment(regularSlots[0].start, 'HH:mm:ss').format('HH:mm') +
                                            ' ' +
                                            t('to').toLowerCase() +
                                            ' ' +
                                            moment(regularSlots[0].end, 'HH:mm:ss').format('HH:mm')}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                    <div className={styles.CheckboxContainer}>
                        <Checkbox
                            text={t('selectOtherDates')}
                            checked={chooseDate}
                            onChange={(checked: boolean) => handleCheckChooseDate(checked)}
                            className={styles.Checkbox}
                            parent={isParent}
                        />
                        <Checkbox
                            text={t('selectPeriod')}
                            checked={selectPeriod}
                            onChange={(checked: boolean) => handleCheckSelectPeriod(checked)}
                            className={styles.Checkbox}
                            parent={isParent}
                        />
                    </div>
                    <div className={styles.BreakLine}></div>
                    <p className={styles.Text}>{isParent ? t('requestAbsenceMessage') : t('declareAbsenceMessage')}</p>
                    {regularSlots.length > 1 && (
                        <SelectMultiple
                            selectOptions={selectOptionsChild}
                            selectValues={selectChilds}
                            setSelectValues={setSelectChilds}
                            className={styles.UserInput}
                            label={t('childs')}
                            isParent={isParent}
                        />
                    )}
                    <div className={styles.TextAreaInput}>
                        <p className={isParent ? styles.TextAreaTitleParent : styles.TextAreaTitleDaycare}>
                            {t('absenceReason')}
                        </p>
                        <TextArea
                            placeholder={t('absenceReason')}
                            {...textAreaProps}
                            className={styles.UserInput}
                            parent={isParent}
                        />
                    </div>
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'custom'}
                            customText={t('yes')}
                            customIcon={<DoDisturbOn />}
                            onClick={isParent ? requestAllAbsences : declareAllAbsences}
                            className={styles.Button}
                            parent={isParent}
                            disabled={
                                loadingDeclare ||
                                loadingMultipleDeclare ||
                                loadingRequest ||
                                loadingMultipleRequest ||
                                loadingRegularSlots
                            }
                        />
                        <ButtonTextIcon
                            type={'custom'}
                            customText={t('no')}
                            customIcon={<Close />}
                            onClick={handleClose}
                            className={styles.Button}
                            parent={isParent}
                        />
                    </div>
                    {!isParent && moment(startDate, 'DD-MM-YYYY').isSameOrAfter(moment(), 'day') && (
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'custom'}
                                customText={t('yesAndTransform')}
                                customIcon={<Transform />}
                                onClick={() => setTransformRegularSlotDialogOpen(true)}
                                className={styles.ButtonFullWidth}
                                disabled={
                                    loadingDeclare ||
                                    loadingMultipleDeclare ||
                                    loadingRequest ||
                                    loadingMultipleRequest ||
                                    loadingRegularSlots
                                }
                            />
                        </div>
                    )}
                    {predefinedMessageDialog}
                </DialogBody>
            </Dialog>
            {isTransformRegularSlotDialogOpen && (
                <TransformRegularSlot
                    open={isTransformRegularSlotDialogOpen}
                    regularSlots={regularSlots.filter((slot: RegularSlot): boolean =>
                        selectChilds.includes(slot.childId.toString())
                    )}
                    dateList={getSlotToRequestAllAbsences(
                        selectChilds,
                        selectPeriod,
                        startDate,
                        endDate,
                        selectedDays,
                        dateList,
                        regularSlotsInCalendar,
                        closingDays
                    ).map((slotRequested: slotIdAndDateAndChildId): string => slotRequested.date)}
                    handleClose={(): void => setTransformRegularSlotDialogOpen(false)}
                    daycare={daycare}
                    date={date}
                    confirmAllRegularSlots={declareAllAbsences}
                    isRpeOrLaep={isRpeOrLaep}
                    places={places}
                />
            )}
            {SelectMultipleDateDialog}
        </>
    )
}

export default DeclareAbsenceRegularSlot

function getSlotToRequestAllAbsences(
    selectChilds: string[],
    selectPeriod: boolean,
    startDate: string | null,
    endDate: string | null,
    selectedDays: string[],
    dateList: string[],
    regularSlotsInCalendar: slotIdAndDateAndChildId[],
    closingDays: ClosingDay[]
): slotIdAndDateAndChildId[] {
    const slotToRequest: slotIdAndDateAndChildId[] = []

    const dateListToDelete: string[] =
        selectPeriod && startDate && endDate
            ? getDatesBetweenTwoDates(startDate, endDate, selectedDays, closingDays)
            : dateList

    dateListToDelete.forEach((date: string): void => {
        regularSlotsInCalendar
            .filter((slot: slotIdAndDateAndChildId): boolean => selectChilds.includes(slot.childId.toString()))
            .filter((slot: slotIdAndDateAndChildId): boolean => slot.date === date)
            .forEach((slot: slotIdAndDateAndChildId): void => {
                slotToRequest.push(slot)
            })
    })

    return slotToRequest
}
